import type { Card, DeckEntry } from './types';

export class DeckManager {
  private deck: Map<number, number> = new Map();
  private cards: Map<number, Card> = new Map();
  private onChange?: () => void;
  private deckName: string = 'Mon Deck';

  constructor(cards: Card[], onChange?: () => void) {
    cards.forEach(card => this.cards.set(card.number, card));
    this.onChange = onChange;
    this.loadFromStorage();
  }

  addCard(cardNumber: number, quantity: number = 1): boolean {
    const card = this.cards.get(cardNumber);
    if (!card) return false;

    // Tokens are not playable cards and must not be added to decks
    const cardType = String(card.type || '').toLowerCase();
    if (cardType === 'token') {
      return false;
    }

    const currentQty = this.deck.get(cardNumber) || 0;
    const newQty = Math.max(0, currentQty + quantity);

    // Limites de copies par carte: Rares = 2, Communes = 4
    const rarity = (card.rarity || 'C').toUpperCase();
    const maxCopies = rarity === 'R' ? 2 : 4;
    if (newQty > maxCopies) {
      return false;
    }

    if (newQty === 0) {
      this.deck.delete(cardNumber);
    } else {
      this.deck.set(cardNumber, newQty);
    }

    this.saveToStorage();
    this.onChange?.();
    return true;
  }

  removeCard(cardNumber: number, quantity: number = 1): void {
    this.addCard(cardNumber, -quantity);
  }

  clear(): void {
    this.deck.clear();
    this.saveToStorage();
    this.onChange?.();
  }

  getDeck(): DeckEntry[] {
    return Array.from(this.deck.entries()).map(([number, qty]) => ({ number, qty }));
  }

  getTotalCards(): number {
    return Array.from(this.deck.values()).reduce((sum, qty) => sum + qty, 0);
  }

  getTotalRares(): number {
    let total = 0;
    this.deck.forEach((qty, cardNumber) => {
      const card = this.cards.get(cardNumber);
      if (card?.rarity === 'R') {
        total += qty;
      }
    });
    return total;
  }

  getCardQuantity(cardNumber: number): number {
    return this.deck.get(cardNumber) || 0;
  }

  isLegal(): { legal: boolean; issues: string[] } {
    const issues: string[] = [];
    const total = this.getTotalCards();
    const rares = this.getTotalRares();

    if (total !== 40) {
      issues.push(`Le deck doit contenir exactement 40 cartes (actuellement: ${total})`);
    }

    if (rares > 12) {
      issues.push(`Maximum 12 cartes rares (actuellement: ${rares})`);
    }

    // Vérifier les limites par carte
    this.deck.forEach((qty, cardNumber) => {
      const card = this.cards.get(cardNumber);
      const rarity = card?.rarity?.toUpperCase() || 'C';
      const maxCopies = rarity === 'R' ? 2 : 4;
      
      if (qty > maxCopies) {
        issues.push(`${card?.name || cardNumber}: maximum ${maxCopies} copies (actuellement: ${qty})`);
      }
    });

    return {
      legal: issues.length === 0,
      issues
    };
  }

  setDeckName(name: string): void {
    this.deckName = name.trim() || 'Mon Deck';
    this.saveToStorage();
  }

  getDeckName(): string {
    return this.deckName;
  }

  importFromText(text: string): { success: boolean; message: string } {
    try {
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      const newDeck = new Map<number, number>();
      let importedName = this.deckName;

      for (const line of lines) {
        // Extraire le nom du deck (ligne commençant par #)
        if (line.startsWith('#')) {
          const match = line.match(/^#\s*(.+?)(?:\s*\(\d+\/\d+\))?$/);
          if (match && match[1]) {
            importedName = match[1].trim();
          }
          continue;
        }

        // Parser les cartes (format: 4x [001] Nom - R)
        const match = line.match(/^(\d+)\s*x\s*\[(\d+)\]/i);
        if (!match) continue;

        const qty = parseInt(match[1], 10);
        const cardNum = parseInt(match[2], 10);

        if (!this.cards.has(cardNum)) {
          return {
            success: false,
            message: `Carte [${String(cardNum).padStart(3, '0')}] introuvable`
          };
        }

        newDeck.set(cardNum, (newDeck.get(cardNum) || 0) + qty);
      }

      if (newDeck.size === 0) {
        return {
          success: false,
          message: 'Aucune carte trouvée dans la decklist'
        };
      }

      // Importer le deck
      this.deck = newDeck;
      this.deckName = importedName;
      this.saveToStorage();
      this.onChange?.();

      const total = this.getTotalCards();
      return {
        success: true,
        message: `Deck importé : ${total} cartes`
      };
    } catch (e) {
      return {
        success: false,
        message: 'Erreur lors de l\'import'
      };
    }
  }

  private saveToStorage(): void {
    try {
      const data = {
        deck: this.getDeck(),
        deckName: this.deckName,
        timestamp: Date.now()
      };
      localStorage.setItem('deckbuilder_deck', JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save deck:', e);
    }
  }

  private loadFromStorage(): void {
    try {
      const saved = localStorage.getItem('deckbuilder_deck');
      if (saved) {
        const data = JSON.parse(saved);
        this.deck.clear();
        data.deck?.forEach((entry: DeckEntry) => {
          if (this.cards.has(entry.number)) {
            this.deck.set(entry.number, entry.qty);
          }
        });
        if (data.deckName) {
          this.deckName = data.deckName;
        }
      }
    } catch (e) {
      console.error('Failed to load deck:', e);
    }
  }

  exportText(): string {
    const total = this.getTotalCards();
    let text = `# ${this.deckName} (${total}/40)\n\n`;
    
    const entries = this.getDeck().sort((a, b) => a.number - b.number);
    
    entries.forEach(entry => {
      const card = this.cards.get(entry.number);
      if (card) {
        const num = String(card.number).padStart(3, '0');
        text += `${entry.qty}x [${num}] ${card.name} - ${card.rarity}\n`;
      }
    });

    return text;
  }
}
